/**
 * EditorNavigationControls
 *
 * React-based pan D-pad and zoom controls for the level editor.
 * Replaces the old Phaser-based camera controls that broke on window resize.
 *
 * Positioned via CSS so it always stays in the correct viewport position
 * regardless of resolution changes.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ServiceRegistry } from '../../../core/ServiceRegistry';
import { EditorSystem } from '../../../systems/editor/EditorSystem';

interface EditorNavigationControlsProps {
  /** Optional external zoom value for display sync */
  zoom?: number;
}

const BUTTON_SIZE = 42;
const ZOOM_STEP = 0.1;

const buttonStyle: React.CSSProperties = {
  width: BUTTON_SIZE,
  height: BUTTON_SIZE,
  backgroundColor: 'rgba(40, 40, 40, 0.85)',
  color: '#ffffff',
  border: '1px solid rgba(100, 100, 100, 0.6)',
  borderRadius: 6,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '20px',
  fontFamily: 'monospace',
  userSelect: 'none',
  transition: 'background-color 0.15s',
};


export const EditorNavigationControls: React.FC<EditorNavigationControlsProps> = ({ zoom: externalZoom }) => {
  const [zoomLevel, setZoomLevel] = useState(externalZoom ?? 1);
  const [cameraPos, setCameraPos] = useState({ x: 0, y: 0 });

  const getEditor = useCallback((): EditorSystem | null => {
    return ServiceRegistry.getInstance().get('EditorSystem') ?? null;
  }, []);

  // Sync zoom from external sources (wheel zoom events)
  useEffect(() => {
    if (externalZoom !== undefined) {
      setZoomLevel(externalZoom);
    }
  }, [externalZoom]);

  // Poll camera position for coordinate display
  useEffect(() => {
    const interval = setInterval(() => {
      const editor = getEditor();
      if (editor) {
        const pos = editor.getCameraPosition();
        setCameraPos(pos);
        setZoomLevel(editor.getZoom());
      }
    }, 200);
    return () => clearInterval(interval);
  }, [getEditor]);

  const handlePan = useCallback((dx: number, dy: number) => {
    getEditor()?.panCamera(dx, dy);
  }, [getEditor]);

  const handleZoom = useCallback((amount: number) => {
    getEditor()?.adjustZoom(amount);
  }, [getEditor]);

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        zIndex: 1100,
        pointerEvents: 'auto',
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* D-Pad */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(3, ${BUTTON_SIZE}px)`, gridTemplateRows: `repeat(3, ${BUTTON_SIZE}px)`, gap: 3 }}>
        {/* Row 1 */}
        <div />
        <button style={buttonStyle} onClick={() => handlePan(0, -1)} title="Pan Up">↑</button>
        <div />
        {/* Row 2 */}
        <button style={buttonStyle} onClick={() => handlePan(-1, 0)} title="Pan Left">←</button>
        <div style={{ ...buttonStyle, backgroundColor: 'rgba(30, 30, 30, 0.5)', border: 'none', cursor: 'default', fontSize: '10px', color: '#888' }}>
          PAN
        </div>
        <button style={buttonStyle} onClick={() => handlePan(1, 0)} title="Pan Right">→</button>
        {/* Row 3 */}
        <div />
        <button style={buttonStyle} onClick={() => handlePan(0, 1)} title="Pan Down">↓</button>
        <div />
      </div>

      {/* Zoom Controls */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button style={buttonStyle} onClick={() => handleZoom(-ZOOM_STEP)} title="Zoom Out">−</button>
        <div style={{
          padding: '4px 10px',
          backgroundColor: 'rgba(30, 30, 30, 0.8)',
          borderRadius: 4,
          color: '#aaa',
          fontSize: '12px',
          fontFamily: 'monospace',
          minWidth: 60,
          textAlign: 'center',
        }}>
          {zoomLevel.toFixed(1)}x
        </div>
        <button style={buttonStyle} onClick={() => handleZoom(ZOOM_STEP)} title="Zoom In">+</button>
        <button style={buttonStyle} onClick={() => getEditor()?.fitToView()} title="Fit Map to View">⛶</button>
      </div>

      {/* Coordinates */}
      <div style={{
        fontSize: '11px',
        color: '#777',
        fontFamily: 'monospace',
        textAlign: 'center',
      }}>
        x:{Math.round(cameraPos.x)} y:{Math.round(cameraPos.y)}
      </div>
    </div>
  );
};
